import { getCartService } from "../../api/cartApi";
import { COUNTRIES } from "../../constants/common/common";
import { checkCartServiceabilityStatus } from "../checkout/deliveryAddress";
import { isServiceabilityItemFullyUnavailable } from "../checkout/serviceabilityForPincode";
import { getLatLongFromSavedAddressSync } from "../../store/sagas/helpers/savedAddressLatLong";

/** In-flight validation keyed by pincode to prevent duplicate API calls. */
let inFlightValidationByPincode = null;

/** Last completed validation result keyed by pincode (reused on Confirm). */
let lastValidationByPincode = null;

/**
 * Sync one-shot skip for Change Location → synthetic Confirm.
 * Redux `skipCartValidationOnce` can lag behind setTimeout(0) click before re-render.
 */
let skipCartValidationOnceSync = false;

/**
 * Arms a one-shot skip of cart serviceability validation (consumed on next validate call).
 * @returns {void}
 */
export const armSkipCartValidationOnce = () => {
  skipCartValidationOnceSync = true;
};

/**
 * Consumes the sync skip flag if set.
 * @returns {boolean}
 */
export const consumeSkipCartValidationOnce = () => {
  if (!skipCartValidationOnceSync) {
    return false;
  }
  skipCartValidationOnceSync = false;
  return true;
};

/**
 * Returns true when any serviceability item is fully unserviceable:
 * `isDeliverable === false` and no `earliestAvailableDate`.
 * Covers both partial and full cart unserviceability.
 *
 * @param {Array} serviceabilityItems
 * @returns {boolean}
 */
export const hasUnserviceableCartItems = (serviceabilityItems = []) => {
  if (!Array.isArray(serviceabilityItems) || !serviceabilityItems.length) {
    return false;
  }

  return serviceabilityItems.some(isServiceabilityItemFullyUnavailable);
};

/**
 * Filters cart order list to items that are fully unserviceable at the selected pincode.
 * Match rule: `isDeliverable === false` and no `earliestAvailableDate`.
 *
 * @param {Array} orderList - Cart order list entries.
 * @param {Array} serviceabilityItems - Serviceability API items.
 * @returns {Array} Cart entries that are out of stock / unserviceable.
 */
export const getFullyUnavailableCartItems = (orderList = [], serviceabilityItems = []) => {
  if (!Array.isArray(orderList) || !orderList.length || !Array.isArray(serviceabilityItems)) {
    return [];
  }

  const unavailablePids = new Set(
    serviceabilityItems
      .filter(isServiceabilityItemFullyUnavailable)
      .map((item) => item?.pid)
      .filter(Boolean),
  );

  if (!unavailablePids.size) {
    return [];
  }

  return orderList.filter((order) => unavailablePids.has(order?.giftItem?.mainproduct?.productId));
};

/**
 * Returns true when any cart order belongs to a different catalog than the current page.
 * Used when the user lands on another country URL with an existing cart.
 *
 * @param {Array} cartItems - Cart order list entries.
 * @param {string} catalogId - Current page catalog id (`appConfig.catalogId`).
 * @returns {boolean}
 */
export const hasCartItemsFromDifferentCatalog = (cartItems = [], catalogId = "") => {
  if (!catalogId || !Array.isArray(cartItems) || !cartItems.length) {
    return false;
  }

  return cartItems.some((order) => {
    const itemCatalog = order?.giftItem?.productCatalogId || order?.giftItem?.catalogId || "";
    return Boolean(itemCatalog) && itemCatalog !== catalogId;
  });
};

/**
 * Resolves ClearCart UI mode after a location/pincode change.
 * Different catalog → country-change UI (all items). Same catalog → OOS UI (filtered items).
 *
 * @param {Object} params
 * @param {Array} params.orderList - Cart order list.
 * @param {string} params.catalogId - Current page catalog id.
 * @param {Array} [params.serviceabilityItems] - Serviceability API items.
 * @returns {{ isCountryChange: boolean, unserviceableItems: Array }}
 */
export const resolveClearCartModeForLocation = ({ orderList = [], catalogId = "", serviceabilityItems = [] }) => {
  if (hasCartItemsFromDifferentCatalog(orderList, catalogId)) {
    return { isCountryChange: true, unserviceableItems: [] };
  }

  return {
    isCountryChange: false,
    unserviceableItems: getFullyUnavailableCartItems(orderList, serviceabilityItems),
  };
};

/**
 * Validates whether existing cart items are serviceable at a newly selected pincode.
 *
 * Fetches the latest cart (when FUS cart count > 0), then calls
 * `POST /serviceability/v1/products/serviceable` with the existing payload mapping
 * and the newly selected pincode.
 *
 * Returns `shouldProceed: false` when any cart item is unserviceable:
 * `isDeliverable === false && earliestAvailableDate == null` — partial or full cart.
 * Caller decides when to open ClearCart (typically on Confirm).
 *
 * Does not break the location-selection flow on Cart Item or Serviceability API failures.
 *
 * @param {Object} params
 * @param {string|number} params.selectedPincode - Newly selected pincode.
 * @param {number} params.cartItemCount - Cart item count from FUS (`state.cart.cartCount`).
 * @param {string} [params.catalogId] - Active catalog id (domestic vs international).
 * @param {Array} [params.savedAddresses] - Saved addresses used to resolve lat/long.
 * @param {boolean} [params.isFromCheckout=false] - Checkout uses its own serviceability path.
 * @param {boolean} [params.isCurrentProductServiceable=true] - Skip when caller already knows the current product is unserviceable.
 * @returns {Promise<{
 *   shouldProceed: boolean,
 *   skipped?: boolean,
 *   orderList?: Array,
 *   serviceabilityResult?: { status: string, items: Array }|null,
 * }>}
 */
export const validateCartServiceabilityOnLocationChange = async ({
  selectedPincode,
  cartItemCount,
  catalogId,
  savedAddresses,
  isFromCheckout = false,
  isCurrentProductServiceable = true,
}) => {
  if (consumeSkipCartValidationOnce()) {
    return { shouldProceed: true, skipped: true };
  }

  if (isFromCheckout || !selectedPincode || !(cartItemCount > 0)) {
    return { shouldProceed: true, skipped: true };
  }

  // Existing PDP non-serviceable UI handles the current product; avoid duplicate cart APIs.
  if (!isCurrentProductServiceable) {
    return { shouldProceed: true, skipped: true };
  }

  const pincodeKey = String(selectedPincode);
  if (inFlightValidationByPincode?.pincode === pincodeKey) {
    return inFlightValidationByPincode.promise;
  }

  if (lastValidationByPincode?.pincode === pincodeKey) {
    return lastValidationByPincode.result;
  }

  const validationPromise = (async () => {
    try {
      const isDomestic = catalogId === COUNTRIES.INDIA;
      const latLong = getLatLongFromSavedAddressSync(savedAddresses);
      const cartResponse = await getCartService({ payload: "Y", isDomestic, ...latLong });

      if (!cartResponse?.data?.isSuccess) {
        return { shouldProceed: true, skipped: false, orderList: [] };
      }

      const orderList = cartResponse?.data?.orderList || [];
      if (!orderList.length) {
        return { shouldProceed: true, orderList };
      }

      const serviceabilityResult = await checkCartServiceabilityStatus(orderList, pincodeKey);

      // Serviceability API failure — reuse existing handling (do not block location update).
      if (serviceabilityResult === null) {
        return { shouldProceed: true, orderList, serviceabilityResult: null };
      }

      // Partial or full cart unserviceable — caller may open ClearCart on Confirm.
      if (hasUnserviceableCartItems(serviceabilityResult.items)) {
        return { shouldProceed: false, orderList, serviceabilityResult };
      }

      return { shouldProceed: true, orderList, serviceabilityResult };
    } catch (error) {
      console.log(error);
      return { shouldProceed: true, error };
    }
  })();

  inFlightValidationByPincode = { pincode: pincodeKey, promise: validationPromise };

  try {
    const result = await validationPromise;
    lastValidationByPincode = { pincode: pincodeKey, result };
    return result;
  } finally {
    if (inFlightValidationByPincode?.promise === validationPromise) {
      inFlightValidationByPincode = null;
    }
  }
};
